// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Layout wrapper class encapsulating layout details required to be passed
 * to stepped container web component.
 * @author James Reid
 */

// @ts-check

// @@imports-types
import { stepRendererCb, StepType, STEP_TYPES } from "../types/index.js"

// @@body
class Layout {
    #renderer; #unmount; #id; #title; #name; #type

    /**
     * Instantiate layout layout wrapper.
     * @param {stepRendererCb} renderer Layout layout render function.
     * @param {object} obj Optional destructured parameters object.
     * @param {string} obj.id Layout id to reference step in code.
     * @param {string} obj.title Long step title to be used in header of step
     * when active in stepped container.
     * @param {string} obj.name Short step name to be used in top progress bar
     * of stepped container.
     * @param {StepType} [obj.type] Layout type in stepped container flow.
     */
    constructor(renderer, { id, title, name, type = STEP_TYPES.step, unmount = () => {} }) {
        // Set layout renderer method as private property to be called.
        this.#renderer = renderer
        this.#unmount = unmount

        // Set layout details as private property.
        this.#id = id
        this.#title = title
        this.#name = name
        this.#type = type
    }

    /**
     * Render layout content inside content container wrapper and load content
     * into stepped container.
     * @returns {void}
     */
    render() { return this.#renderer() }

    unmount() { return this.#unmount() }

    /**
     * Getter for private layout id property.
     */
    get id() { return this.#id }

    /**
     * Getter for private layout title property.
     */
    get title() { return this.#title }

    /**
     * Getter for private layout name property.
     */
    get name() { return this.#name }

    /**
     * Getter for private layout type property.
     */
    get type() { return this.#type }
}

// @@exports
export { Layout }
